import { mixColors, rgbToHex, RGB } from '@src/utils/colors';
import { LinearCpmRow, MetaDemo } from './linear-cpms-constants';

const ROTATION_SORT_ORDER = [
  'Early Morning',
  'Daytime',
  'Early Fringe',
  'Prime',
  'Late Fringe',
  'Overnight',
  'Broad',
  'Weekend'
];

const determineSortOrder = (rotation: string): number => {
  const index = ROTATION_SORT_ORDER.findIndex(pattern => rotation.includes(pattern));
  return index === -1 ? ROTATION_SORT_ORDER.length : index;
};

export const sortLinearCpmRows = (rows: LinearCpmRow[]): LinearCpmRow[] =>
  [...rows].sort((a, b) => {
    if (a.network !== b.network) return a.network.localeCompare(b.network);
    if (a.avail !== b.avail) return a.avail.localeCompare(b.avail);
    const orderDiff = determineSortOrder(a.rotation) - determineSortOrder(b.rotation);
    if (orderDiff !== 0) return orderDiff;
    return a.rotation.localeCompare(b.rotation);
  });

// Matches bpm-frontend: a falsy value (including 0, not just non-finite ones) is excluded
// from the range.
export const minMaxForField = (
  rows: LinearCpmRow[],
  field: 'aa' | 'cpm'
): { min?: number; max?: number } => {
  const values = rows.map(row => row[field]).filter(value => value && Number.isFinite(value));
  if (values.length === 0) return {};
  return { min: Math.min(...values), max: Math.max(...values) };
};

const WHITE: RGB = { r: 255, g: 255, b: 255 };

// Same mechanics as MetricsTable's sequential heat map (metrics-table-utils.ts#getHeatMapInfo):
// clamp the value's position in [min, max] to [0, 1], then mix from white (low) to `highColor`
// (high) by that fraction.
export const getHeatColor = (value: number, min: number, max: number, highColor: RGB): string => {
  const pct = Math.max(0, Math.min(1, (value - min) / (max - min)));
  return rgbToHex(mixColors(WHITE, highColor, pct));
};

// The "quick preset" dropdown's value: which preset (if any) the current demo selection
// exactly matches, order-independent. Editing the demo dropdown by hand away from a preset's
// exact set naturally falls back to "no match", which renders as the placeholder.
export const findMatchingPreset = (demos: string[], presets: MetaDemo[]): MetaDemo | undefined => {
  const demoSet = new Set(demos);
  return presets.find(
    preset => preset.demos.length === demos.length && preset.demos.every(demo => demoSet.has(demo))
  );
};
